import { useNavigate } from "react-router-dom";
import { ArrowRight, Search, MapPin, Wrench, Truck, Sparkles, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Home = () => {
  const navigate = useNavigate();

  const steps = [
    {
      n: "01",
      title: "Request",
      desc: "Tell us exactly what you want — make, model, color, budget, features.",
      icon: Search,
    },
    {
      n: "02",
      title: "Find",
      desc: "We search available auction vehicles across our dealer network.",
      icon: MapPin,
      img: "https://plus.unsplash.com/premium_photo-1664300368336-358645fa4c64?fm=jpg&q=80&w=1600&auto=format&fit=crop",
    },
    {
      n: "03",
      title: "Prepare",
      desc: "We purchase, inspect, and coordinate any needed repairs.",
      icon: Wrench,
      img: "https://images.unsplash.com/photo-1632405862117-236585cfb757?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBhdXRvJTIwcmVwYWlyJTIwbWVjaGFuaWMlMjB3b3Jrc2hvcCUyMGN1c3RvbSUyMHR1bmluZ3xlbnwwfHx8fDE3OTEyNDkwODF8MA&ixlib=rb-4.1.0&q=85",
    },
    {
      n: "04",
      title: "Deliver",
      desc: "We handle logistics and drop the vehicle at your door.",
      icon: Truck,
      img: "https://images.unsplash.com/photo-1764200458388-65c4b0c19a95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjYXIlMjBhdWN0aW9uJTIwYXV0byUyMHRyYW5zcG9ydCUyMGZsYXRiZWQlMjB0cmFpbGVyJTIwZGVsaXZlcnl8ZW58MHx8fHwxNzkxMjQ5MDgxfDA&ixlib=rb-4.1.0&q=85",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-slate-100">
      <Navbar transparent />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden hero-radial">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 sm:pt-20 sm:pb-28 lg:pt-28 lg:pb-36 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-400 text-[11px] uppercase tracking-widest font-semibold">
              <Sparkles className="w-3 h-3" /> Concierge Auction Service
            </div>
            <h1 className="font-display mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight">
              GETEASY <span className="gold-text">CAR</span>
            </h1>
            <p className="font-display mt-4 text-xl sm:text-2xl lg:text-3xl font-semibold uppercase tracking-wide text-slate-300">
              Find it. <span className="text-amber-400">Fix it.</span> Drive it.
            </p>
            <p className="mt-6 text-base sm:text-lg text-slate-400 max-w-2xl leading-relaxed">
              Tell us the vehicle you want. We find it at auction, handle the process,
              repair it when needed, and deliver it to you.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                data-testid="hero-find-my-car-btn"
                onClick={() => navigate("/find-my-car")}
                className="group inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-black font-extrabold uppercase tracking-wider px-8 py-4 rounded-xl shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98]"
              >
                Find My Car
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-track-request-btn"
                onClick={() => navigate("/track")}
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white font-semibold uppercase tracking-wider px-8 py-4 rounded-xl border border-white/15 backdrop-blur-md transition-all active:scale-[0.98]"
              >
                Track My Request
              </button>
            </div>

            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-400" /> Transparent pricing
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-400" /> Pre-delivery inspection
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-400" /> Door-to-door delivery
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-amber-500/5">
              <img
                src="https://images.unsplash.com/photo-1615884363252-983eed162938?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBkYXJrJTIwbmlnaHQlMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzkxMjQ5MDgxfDA&ixlib=rb-4.1.0&q=85"
                alt="Luxury sports car at night"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/30 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="font-mono-brand text-[10px] uppercase tracking-widest text-amber-400">
                  Live Auction Access
                </div>
                <div className="font-display text-2xl font-bold uppercase mt-1">
                  Over 40,000 vehicles weekly
                </div>
              </div>
            </div>
            <div className="hidden lg:block absolute -z-10 -top-10 -right-10 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
                / The Process
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight max-w-2xl">
                How it <span className="gold-text">works</span>
              </h2>
            </div>
            <p className="text-slate-400 max-w-md">
              Four stages. Zero stress. From the first request to a key handoff on your
              driveway — we handle every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {steps.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`how-it-works-step-${s.n}`}
                  className="group relative bg-gradient-to-b from-[#18181E] to-[#121216] border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-amber-500/40 hover:shadow-[0_0_40px_rgba(245,158,11,0.08)]"
                >
                  {s.img && (
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={s.img}
                        alt={s.title}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="font-mono-brand text-xs text-amber-400">
                        / {s.n}
                      </div>
                      <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400 group-hover:bg-amber-500 group-hover:text-black transition">
                        <Icon className="w-4 h-4" />
                      </div>
                    </div>
                    <h3 className="font-display text-xl font-bold uppercase tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- CTA BAND ---------- */}
      <section className="py-16 sm:py-20 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden bg-[#121216] border border-white/10 rounded-3xl p-8 sm:p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
            <div className="max-w-xl relative">
              <div className="text-xs uppercase tracking-[0.25em] font-semibold text-amber-400">
                / Ready?
              </div>
              <h3 className="font-display mt-3 text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">
                Tell us the car. <br />
                <span className="gold-text">We’ll handle the rest.</span>
              </h3>
              <p className="mt-4 text-slate-400">
                Submit your request in under 2 minutes. You’ll get a tracking ID instantly.
              </p>
            </div>
            <div className="flex gap-3 relative">
              <button
                data-testid="cta-find-my-car-btn"
                onClick={() => navigate("/find-my-car")}
                className="bg-amber-500 hover:bg-amber-600 text-black font-extrabold uppercase tracking-wider px-7 py-3.5 rounded-xl shadow-lg shadow-amber-500/20 transition active:scale-[0.98]"
              >
                Find My Car
              </button>
              <button
                data-testid="cta-track-btn"
                onClick={() => navigate("/track")}
                className="bg-white/5 hover:bg-white/10 text-white font-semibold uppercase tracking-wider px-7 py-3.5 rounded-xl border border-white/15 transition"
              >
                Track
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="hidden">reserved</footer>
      <Footer />
    </div>
  );
};

export default Home;
